import { useCallback, useMemo, useState } from "react";
import {
  apiErrorSlug,
  InboxItem,
  isApiErrorLike,
  type ApiFetchWith,
  type FetchPage,
} from "@heraion/shared";

import { useAsyncList } from "../useAsyncList";

// GET /api/me/inbox?audience=&limit=&cursor= (SPEC — fetta 1). Rows per
// page for the Home fold — a handful, not the athlete's whole history; a
// future standalone Inbox screen can raise this without touching the fetch
// shape (measurementsPage.ts convention: the page size is a concern of the
// CALLER, not of the pagination primitive).
const INBOX_PAGE_SIZE = 20;

// Rows that fail the schema are DROPPED, not thrown on (measurementsPage.ts
// convention): one malformed row must not blank the whole section.
function parseInboxItem(raw: unknown): InboxItem | null {
  const parsed = InboxItem.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

// URLSearchParams is available here (this module targets the DOM/RN
// runtime, not the platform-neutral shared package), so it escapes the
// opaque cursor for us.
// INBOX-10: one read for every role (`audience=all`). The item carries its
// own `audience`, and the card prints it; there is no per-role fetch left.
function inboxPagePath(cursor: string | null): string {
  const qs = new URLSearchParams({ audience: "all", limit: String(INBOX_PAGE_SIZE) });
  if (cursor) qs.set("cursor", cursor);
  return `/api/me/inbox?${qs.toString()}`;
}

/** "snooze" is the MAX proposal's "Bad day" (#182), and nothing else's. */
export type InboxDecision = "accept" | "reject" | "snooze";

// A stored row keeps its id when a newer one replaces it (maxBump:<slug>,
// #182), so what was answered is the id at that createdAt.
const answeredKey = (it: InboxItem) => `${it.id}@${it.createdAt}`;

export type UseInboxOptions = {
  /** Host-injected client (mobile/web-rnw each bring their own Cognito
   *  fetch wrapper), house convention, same DI slot as useLibrary/usePlans. */
  apiFetch: ApiFetchWith;
  /** #182: a MAX was written by accepting a proposal. The host re-reads
   *  its MAXes (mobile: MaxesContext; web: every mounted copy). */
  onMaxWritten?: () => void;
};

/**
 * Data + actions for the caller's inbox, every audience at once. Built on
 * useAsyncList (the cursor-paginated primitive lifted 2026-09-18) rather than a hand-rolled
 * loop — see next/inbox-and-notifications.md §SPEC, "useInbox su useAsyncList".
 */
export function useInbox({ apiFetch, onMaxWritten }: UseInboxOptions) {
  const [serverTotal, setServerTotal] = useState<number | null>(null);
  const [resolvedSinceTotal, setResolvedSinceTotal] = useState(0);
  const fetchPage: FetchPage<InboxItem, string> = useCallback(
    async (cursor) => {
      const raw = await apiFetch<unknown>(inboxPagePath(cursor));
      const page = raw as {
        items?: unknown[];
        cursor?: string | null;
        total?: unknown;
      } | null;
      // The server's count of the WHOLE list (INBOX-10), not this page's.
      // Every fresh total already excludes writes that landed, so the local
      // tally of resolutions restarts from it.
      if (typeof page?.total === "number") {
        setServerTotal(page.total);
        setResolvedSinceTotal(0);
      }
      const items: InboxItem[] = [];
      for (const it of page?.items ?? []) {
        const parsed = parseInboxItem(it);
        if (parsed) items.push(parsed);
      }
      return { items, cursor: page?.cursor ?? null };
    },
    [apiFetch],
  );

  const list = useAsyncList<InboxItem, string>({
    key: "inbox:all",
    fetchPage,
  });
  const refreshList = list.refresh;

  // Ids resolved LOCALLY (accepted / rejected / dismissed) — filtered out of
  // the displayed list the instant the write succeeds, rather than waiting
  // on a refetch round trip. This is display-only: the server row is the
  // only truth (no counter kept here either, per the SPEC's "nessun
  // contatore mantenuto"), so a stale entry slipping back in after a real
  // refresh() would just mean this Set forgot it — never a data bug.
  const [resolvedIds, setResolvedIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<Error | null>(null);

  // Deduped by id, first occurrence wins. useAsyncList's loadMore
  // concatenates pages verbatim (packages/ui/src/useAsyncList.ts:199) — it
  // is a SHARED primitive with other callers, so a global dedupe there
  // would be a behaviour change for all of them. This hook is the seam
  // between that primitive and the inbox UI, so the dedupe belongs here:
  // if the underlying list shifts between two page fetches, the same row
  // can arrive twice with the same `id`, and InboxSection keys its cards
  // on that id (InboxSection.tsx:49) — an undeduped list renders the same
  // card twice, both driven by the same `busyId === item.id` check.
  const items = useMemo(() => {
    const base =
      list.items?.filter((it) => !resolvedIds.has(answeredKey(it))) ?? null;
    if (!base) return base;
    const seen = new Set<string>();
    const deduped: InboxItem[] = [];
    for (const it of base) {
      if (seen.has(it.id)) continue;
      seen.add(it.id);
      deduped.push(it);
    }
    return deduped;
  }, [list.items, resolvedIds]);

  const resolveLocally = useCallback((item: InboxItem) => {
    setResolvedIds((prev) => {
      const next = new Set(prev);
      next.add(answeredKey(item));
      return next;
    });
    setResolvedSinceTotal((n) => n + 1);
  }, []);

  // What the bell counts: everything still waiting, beyond the first page.
  // Before the first answer (a cache hydrate) only the loaded rows are known.
  const shown = items?.length ?? 0;
  const count =
    serverTotal === null
      ? shown
      : Math.max(shown, serverTotal - resolvedSinceTotal);

  // ⛔ Direct apiFetch, NOT the durableWrite seam: both actions read the
  // server's verdict synchronously (a rejected coach-edge accept must stay
  // on screen, not vanish optimistically the way a queued write would), and
  // this is an online-only gesture by nature. Same exception useFeedbackInbox's
  // patch() takes — see ../seams.ts and ../FeedbackInbox/useFeedbackInbox.ts.
  const respondToProposal = useCallback(
    async (
      item: InboxItem,
      decision: InboxDecision,
    ): Promise<Error | null> => {
      if (item.kind !== "proposal") return null;
      setBusyId(item.id);
      setActionError(null);
      try {
        // #182: the MAX proposal. The row is the server's; answering it writes
        // the MAX (accept) or keeps it (reject, and "Bad day" = snooze). The
        // row's workoutId tells the server which session the athlete read.
        // Online-only, like every answer here (offline-writes.md): the error
        // pill says a connection is needed. ⚠️ Two writers on one MAX: this
        // accept is not on maxOutbox, so a manual edit still queued there
        // replays AFTER it and wins, whatever the athlete did last
        // (offline-writes.md:188-195). One that lands first removes the row,
        // and the accept finds 404.
        if (item.refType === "maxBump") {
          const workoutId = item.params?.workoutId;
          const body = {
            ...(workoutId ? { workoutId } : {}),
            ...(decision === "snooze" ? { snooze: true } : {}),
          };
          await apiFetch(
            `/api/me/max-bumps/${encodeURIComponent(item.refId)}/${
              decision === "accept" ? "accept" : "reject"
            }`,
            { method: "PATCH", body },
          );
          resolveLocally(item);
          if (decision === "accept") onMaxWritten?.();
          return null;
        }
        if (decision === "snooze") {
          const err = new Error("unsupported_decision");
          setActionError(err);
          return err;
        }
        // Fetta 1's only proposal is the derived coach invite (SPEC: "gli
        // inviti coach NON si duplicano — GET /me/inbox li deriva a lettura
        // dagli edge pending"). Accepting/rejecting it is therefore the SAME
        // machine CoachSettingsScreen already drives (apps/web-rnw/src/
        // CoachSettingsScreen.tsx:81-124) — never a second accept/reject
        // endpoint reinvented for the inbox row.
        if (item.refType === "coachEdge") {
          const path = decision === "accept" ? "accept" : "revoke";
          await apiFetch(
            `/api/me/coach-edges/${encodeURIComponent(item.refId)}/${path}`,
            { method: "PATCH" },
          );
          resolveLocally(item);
          return null;
        }
        // PE-48 fetta B — an offered program/plan. Its state lives on
        // the AssignmentProposal row, not on this inbox item, so the
        // answer goes to that machine's own endpoint; accepting is what
        // triggers the real fan-out, server-side.
        if (item.refType === "assignmentProposal") {
          await apiFetch(
            `/api/me/assignment-proposals/${encodeURIComponent(item.refId)}/${
              decision === "accept" ? "accept" : "reject"
            }`,
            { method: "PATCH" },
          );
          resolveLocally(item);
          return null;
        }
        // A future refType must add its OWN branch here — never a generic
        // "guess the endpoint from refType" fallback (that would let an
        // unvetted refType silently start writing somewhere).
        const err = new Error(`unsupported_ref_type_${item.refType}`);
        setActionError(err);
        return err;
      } catch (e) {
        // A lost-response retry lands here as 409 `not_pending` (the edge
        // was already accepted/revoked by the FIRST attempt's write, which
        // did land — apps/api/src/routes/coachEdges.ts:230-233) or 404
        // `not_found` (the edge is gone by the time this call reached the
        // server). Both mean the proposal is already resolved, just not by
        // THIS attempt's response — so the honest UI move is the same as a
        // real success: drop it locally, no error shown, no retry loop.
        // Two slugs are carved out: `no_longer_assigned` and `item_gone` mean
        // the accept did NOT happen, and a card that leaves would say it did
        // (INBOX-8). They keep the card and go to the error pill instead.
        const slug = apiErrorSlug(e);
        // A newer session replaced the MAX row under the same id: show that
        // one. Resolving locally would hide it.
        if (item.refType === "maxBump" && isApiErrorLike(e) && e.status === 409) {
          if (slug === "superseded") {
            await refreshList();
            return null;
          }
          const err = e instanceof Error ? e : new Error("io_error");
          setActionError(err);
          return err;
        }
        if (
          isApiErrorLike(e) &&
          (e.status === 409 || e.status === 404 || e.status === 410) &&
          slug !== "no_longer_assigned" &&
          slug !== "item_gone"
        ) {
          resolveLocally(item);
          return null;
        }
        const err = e instanceof Error ? e : new Error("io_error");
        setActionError(err);
        return err;
      } finally {
        setBusyId(null);
      }
    },
    [apiFetch, resolveLocally, refreshList, onMaxWritten],
  );

  // PATCH /api/me/inbox/:id/dismiss — the one writer this fetta's UI calls
  // directly (the other, `PATCH /feedback/:id`, is the admin's — out of
  // this component's reach and out of scope for packages/ui).
  const dismissNotice = useCallback(
    async (item: InboxItem): Promise<Error | null> => {
      if (item.kind !== "notice") return null;
      setBusyId(item.id);
      setActionError(null);
      try {
        await apiFetch(
          `/api/me/inbox/${encodeURIComponent(item.id)}/dismiss`,
          { method: "PATCH" },
        );
        resolveLocally(item);
        return null;
      } catch (e) {
        const err = e instanceof Error ? e : new Error("io_error");
        setActionError(err);
        return err;
      } finally {
        setBusyId(null);
      }
    },
    [apiFetch, resolveLocally],
  );

  return {
    items,
    /** Items still waiting in the whole inbox, not just the loaded page. */
    count,
    status: list.status,
    error: list.error,
    hasMore: list.hasMore,
    refresh: list.refresh,
    loadMore: list.loadMore,
    /** The one item with an accept/reject/dismiss write in flight. */
    busyId,
    actionError,
    respondToProposal,
    dismissNotice,
  };
}
